// Global command bar (Ctrl/Cmd+K, or "/" when not typing). Modes: Go (default), Ask ("?"), Do (">").
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

export interface Command {
  id: string;
  label: string;
  group: 'Go' | 'Do';
  hint?: string;
  keywords?: string[];
  run: () => void;
}

export interface CommandPaletteProps {
  commands: Command[];
  /** "?" mode: natural-language request handed to the user's agent. Absent = Ask mode disabled. */
  onAsk?: (text: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Extra "Go" results appended after the static command matches — e.g. a caller-debounced live
   * entity search (features/search useCommandSearch). Already capped by the caller; this component
   * does no fetching of its own. Ignored outside Go mode.
   */
  liveResults?: Command[];
  /** Fires with the raw query text and current mode on every change, so a caller can drive `liveResults`. */
  onQueryChange?: (query: string, mode: 'go' | 'do' | 'ask') => void;
}

function isTypingTarget(el: EventTarget | null) {
  if (!(el instanceof HTMLElement)) return false;
  return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
}

export function score(cmd: Command, q: string): number {
  if (!q) return 1;
  const hay = [cmd.label, ...(cmd.keywords ?? [])].join(' ').toLowerCase();
  const needle = q.toLowerCase();
  if (hay.startsWith(needle)) return 3;
  if (hay.includes(needle)) return 2;
  let i = 0;
  for (const ch of hay) if (ch === needle[i]) i++;
  return i === needle.length ? 1 : 0;
}

export function CommandPalette({
  commands,
  onAsk,
  open: controlled,
  onOpenChange,
  liveResults = [],
  onQueryChange,
}: CommandPaletteProps) {
  const id = useId();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlled ?? internalOpen;
  const setOpen = useCallback(
    (v: boolean) => {
      setInternalOpen(v);
      onOpenChange?.(v);
    },
    [onOpenChange],
  );
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!open);
      } else if (e.key === '/' && !open && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, setOpen]);

  useEffect(() => {
    if (open) {
      opener.current = document.activeElement as HTMLElement | null;
      setQuery('');
      setActive(0);
      inputRef.current?.focus();
    } else if (opener.current) {
      opener.current.focus();
      opener.current = null;
    }
  }, [open]);

  const mode: 'go' | 'do' | 'ask' = query.startsWith('>')
    ? 'do'
    : query.startsWith('?')
      ? 'ask'
      : 'go';
  const q = mode === 'go' ? query.trim() : query.slice(1).trim();

  // Keyed only to [q, mode]: onQueryChange is read from a ref so an unmemoized inline callback from
  // the caller (a fresh function every render) can never turn this into a render loop.
  const onQueryChangeRef = useRef(onQueryChange);
  onQueryChangeRef.current = onQueryChange;
  useEffect(() => {
    onQueryChangeRef.current?.(q, mode);
  }, [q, mode]);

  const results = useMemo(() => {
    if (mode === 'ask') return [];
    const pool = mode === 'do' ? commands.filter((c) => c.group === 'Do') : commands;
    const matched = pool
      .map((c) => ({ c, s: score(c, q) }))
      .filter((x) => x.s > 0)
      .sort((a, b) => b.s - a.s)
      .map((x) => x.c);
    if (mode !== 'go' || !liveResults.length) return matched;
    const seen = new Set(matched.map((c) => c.id));
    return [...matched, ...liveResults.filter((c) => !seen.has(c.id))];
  }, [commands, mode, q, liveResults]);

  const close = () => setOpen(false);
  const runAt = (i: number) => {
    const cmd = results[i];
    if (!cmd) return;
    close();
    cmd.run();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(results.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (mode === 'ask') {
        if (onAsk && q) {
          close();
          onAsk(q);
        }
      } else runAt(active);
    } else if (e.key === 'Tab') {
      // Focus trap: the listbox is driven by aria-activedescendant, so the input is the only stop.
      e.preventDefault();
    }
  };

  if (!open) return null;
  const listId = `${id}-list`;
  const optId = (i: number) => `${id}-opt-${i}`;
  const clamped = Math.min(active, Math.max(results.length - 1, 0));

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center bg-[var(--scrim)] px-3 pt-[10vh] backdrop-blur-[2px]"
      onMouseDown={(e) => e.target === e.currentTarget && close()}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Command bar"
        className="fl-popover w-full max-w-xl overflow-hidden"
      >
        <div className="flex items-center gap-3 border-hair-b px-4">
          <span aria-hidden="true" className="font-mono text-sm text-signal-ink">
            {mode === 'do' ? '>' : mode === 'ask' ? '?' : 'go'}
          </span>
          <input
            ref={inputRef}
            role="combobox"
            aria-label="Command"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={results.length ? optId(clamped) : undefined}
            className="min-h-14 flex-1 bg-transparent font-sans text-lg text-ink outline-none placeholder:text-ink-2"
            placeholder="Jump to…  (> run a command, ? ask your agent)"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={onKeyDown}
          />
          <kbd className="fl-kbd hidden sm:inline-flex">Esc</kbd>
        </div>
        {mode === 'ask' ? (
          <p className="m-0 p-4 text-sm text-ink-2">
            {onAsk
              ? 'Press Enter to send this to your agent. The result arrives in your inbox as a proposal — nothing runs without you.'
              : 'Asking your agent needs an agent token with the right scopes. Create one in the agent console.'}
          </p>
        ) : results.length === 0 ? (
          <p className="m-0 p-4 text-sm text-ink-2">No commands match “{q}”.</p>
        ) : null}
        <ul
          id={listId}
          role="listbox"
          aria-label="Results"
          className="m-0 max-h-[55vh] list-none overflow-y-auto p-2"
        >
          {results.map((c, i) => (
            <li
              key={c.id}
              id={optId(i)}
              role="option"
              aria-selected={i === clamped}
              className="flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-sm px-3 aria-selected:bg-paper-2"
              onMouseEnter={() => setActive(i)}
              onClick={() => runAt(i)}
            >
              <span>{c.label}</span>
              <span className="truncate font-mono text-xs text-ink-3">{c.hint ?? c.group}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
